import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from "@angular/core";
import { Router } from "@angular/router";

import { MessageService } from "primeng/api";

import { RoutesService } from "@main/routes";
import { AdminService } from "@main/admin/services/admin.service";
import { CurrentUserChangedData, UserService } from "@main/users";
import { BroadcastService } from "../broadcasting";
import { AppStorage } from "../../utils/app-storage";
import { MainMenuService } from "@main/menu-designer";
import { StaticPageService } from "@main/static-pages";
import { Message } from "../../classes";


@Injectable({
    providedIn: "root"
})
export class SecurityService {
    public static readonly RoutesReloadedEventName = "RoutesReloaded";

    private readonly allowedPageRoutesKey = "allowed-page-routes";
    private readonly _homeUrl = "/app";

    private allowedPagesPaths: string[] = [];

    constructor(private routesService: RoutesService,
                private adminService: AdminService,
                private broadcastService: BroadcastService,
                private router: Router,
                private environmentInjector: EnvironmentInjector) {
        this.allowedPagesPaths = <string[]>AppStorage.getItem(this.allowedPageRoutesKey);

        //Hardcoded inline constant - as a workaround for modules dependencies issue.
        // TODO: resolve the module dependency issue instead of using the inline constant
        broadcastService.on("MainMenuChangedEventName").subscribe(() => this.refreshRoutes(true));
        broadcastService.on(StaticPageService.StaticPagesChangedEventName).subscribe(() => this.refreshRoutes(true));
    }

    public initialize(): void {
        this.broadcastService.on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName).subscribe(data => {
            if (data.isLogout) {
                this.allowedPagesPaths = null;
            } else {
                if (data.identityChanged || data.accessRightsChanged) {
                    const shouldValidateCurrentRoute = !data.isLogin;
                    this.refreshRoutes(true, shouldValidateCurrentRoute);
                }
            }
        });
    }

    isPageAvailable(path: string[]): boolean {
        if (!this.allowedPagesPaths || !path || path.length == 0) {
            return false;
        }

        if (path.some(p => p.startsWith("/app/admin")) && !this.adminService.accessible) {
            return false;
        }

        return this.allowedPagesPaths.find(allowedPagesPathItem => path.some(pathItem => pathItem === allowedPagesPathItem)) != null;
    }

    refreshRoutes(force = true, shouldValidateCurrentRoute: boolean = false): Promise<string[]> {
        if (force || !this.allowedPagesPaths) {
            return this.routesService.getCurrentUserAllowedRoutesPaths().then(allowedPagesPaths => {
                this.allowedPagesPaths = allowedPagesPaths;
                AppStorage.setItem(this.allowedPageRoutesKey, allowedPagesPaths);
                this.broadcastService.broadcast(SecurityService.RoutesReloadedEventName);

                if (shouldValidateCurrentRoute) {
                    this.validateCurrentRoute();
                }

                return allowedPagesPaths;
            });
        } else {
            return Promise.resolve(this.allowedPagesPaths);
        }
    }

    private validateCurrentRoute(): void {
        const currentUrl = this.router.url;

        if (currentUrl === this._homeUrl || currentUrl.startsWith(this._homeUrl + "?")) {
            return;
        }

        if (!this.isPageAvailable([currentUrl])) {
            //Using EnvironmentInjector to avoid circular dependency for the MessageService
            runInInjectionContext(this.environmentInjector, () => {
                inject(MessageService).add(Message.Error("You do not have the necessary permissions to access this screen."));
            });
            this.router.navigate([this._homeUrl]);
        }
    }
}